import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { StackAuthProvider } from './components/providers/StackAuthProvider';
import { AuthCacheProvider } from './contexts/AuthCacheContext';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import RegularTasksPage from './pages/RegularTasksPage';
import SettingsPage from './pages/SettingsPage';
import { MattersPage } from './pages/MattersPage';
import { ProjectsPage } from './pages/ProjectsPage';
import OverviewPage from './pages/OverviewPage';
import FocusModeV2 from './pages/FocusModeV2';
import ErrorBoundary from './components/ErrorBoundary';
import ClientsPage from './pages/ClientsPage';
import ClientDashboard from './pages/ClientDashboard';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
// Email verification and password reset are not used with local auth
import AuthLayout from './components/layouts/AuthLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedRouteWithLayout from './components/auth/ProtectedRouteWithLayout';
import NotFoundPage from './pages/NotFoundPage';
import DebugAuthPage from './pages/DebugAuthPage';
import RegularTasksFixPage from './pages/RegularTasksFixPage';
import { DependencyFixPage } from './pages/DependencyFixPage';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { PWADebug } from './components/PWADebug';
import InboxPage from './pages/InboxPage';
import TestUploadPage from './pages/TestUploadPage';
import DeadlinesPage from './pages/DeadlinesPage';
import TimerDebugPage from './pages/TimerDebugPage';
import { FloatingTimer } from './components/timer/FloatingTimer';
import { TimerProvider } from './contexts/TimerContext';
import versionData from '../version.json';

// Log version on app start
console.log(`🚀 Goalie4 v${versionData.version} - Build Date: ${versionData.buildDate}`);
console.log(`📝 ${versionData.description}`);

function App() {
  return (
    <ErrorBoundary>
      <StackAuthProvider>
        <AuthCacheProvider>
          <TimerProvider>
            <Router>
              <Toaster position="top-right" />
              <PWAInstallPrompt />
              <PWADebug />
              <FloatingTimer />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
                <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
                { /* Local auth does not use dedicated reset/verify routes */ }
                <Route path="/debug-auth" element={<DebugAuthPage />} />

                {/* Protected routes */}
                <Route path="/overview" element={<ProtectedRouteWithLayout><OverviewPage /></ProtectedRouteWithLayout>} />
                <Route path="/projects" element={<ProtectedRouteWithLayout><ProjectsPage /></ProtectedRouteWithLayout>} />
                <Route path="/matters" element={<ProtectedRouteWithLayout><MattersPage /></ProtectedRouteWithLayout>} />
                <Route path="/dashboard" element={<ProtectedRouteWithLayout><DashboardPage /></ProtectedRouteWithLayout>} />
                <Route path="/daily" element={<ProtectedRouteWithLayout><RegularTasksPage /></ProtectedRouteWithLayout>} />
                <Route path="/settings" element={<ProtectedRouteWithLayout><SettingsPage /></ProtectedRouteWithLayout>} />
                <Route path="/templates" element={<Navigate to="/settings" replace />} />
                <Route path="/focus" element={<ProtectedRouteWithLayout><FocusModeV2 /></ProtectedRouteWithLayout>} />
                <Route path="/clients" element={<ProtectedRouteWithLayout><ClientsPage /></ProtectedRouteWithLayout>} />
                <Route path="/clients/:clientId" element={<ProtectedRouteWithLayout><ClientDashboard /></ProtectedRouteWithLayout>} />
                <Route path="/fix-regular-tasks" element={<ProtectedRouteWithLayout><RegularTasksFixPage /></ProtectedRouteWithLayout>} />
                <Route path="/fix-dependencies" element={<ProtectedRouteWithLayout><DependencyFixPage /></ProtectedRouteWithLayout>} />
                <Route path="/inbox" element={<ProtectedRouteWithLayout><InboxPage /></ProtectedRouteWithLayout>} />
                <Route path="/test-upload" element={<ProtectedRouteWithLayout><TestUploadPage /></ProtectedRouteWithLayout>} />
                <Route path="/deadlines" element={<ProtectedRouteWithLayout><DeadlinesPage /></ProtectedRouteWithLayout>} />
                <Route path="/timer-debug" element={<ProtectedRouteWithLayout><TimerDebugPage /></ProtectedRouteWithLayout>} />

                {/* 404 route */}
                <Route path="/404" element={<NotFoundPage />} />

                {/* Catch all route - redirect to 404 */}
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Router>
          </TimerProvider>
        </AuthCacheProvider>
      </StackAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
